import { apiFetch } from '@/lib/api';

/**
 * Hotel search result from Google Places API
 */
export interface HotelSearchResult {
  placeId: string;
  name: string;
  address: string;
  lat: number;
  lon: number;
  types: string[];
  primaryType?: string;
  rating?: number;
  userRatingCount?: number;
  priceLevel?: string;
  phone?: string;
  website?: string;
}

/**
 * Response from hotel search endpoints
 */
export interface HotelSearchResponse {
  hotels: HotelSearchResult[];
  nextPageToken?: string;
  totalDisplayed: number;
}

/**
 * Location for search
 */
export interface SearchLocation {
  lat: number;
  lon: number;
  radiusMeters?: number;
}

/**
 * Text search parameters
 */
export interface TextSearchParams {
  query: string;
  location?: SearchLocation;
  pageSize?: number;
  pageToken?: string;
}

/**
 * Nearby search parameters
 */
export interface NearbySearchParams {
  location: SearchLocation;
  maxResults?: number;
  rankBy?: 'POPULARITY' | 'DISTANCE';
}

/**
 * Geocode result
 */
export interface GeocodeResult {
  lat: number;
  lon: number;
  name?: string;
  address?: string;
}

/**
 * Existing venue info
 */
export interface ExistingVenueInfo {
  id: string;
  property_name: string;
}

/**
 * Check existing response
 */
export interface CheckExistingResponse {
  existing: Record<string, ExistingVenueInfo>;
  existingCount: number;
}

/**
 * Hotel data for import
 */
export interface HotelImportData {
  placeId: string;
  name: string;
  address: string;
  lat: number;
  lon: number;
  rating?: number;
  userRatingCount?: number;
  phone?: string;
  website?: string;
}

/**
 * Import response
 */
export interface ImportHotelsResponse {
  imported: number;
  skipped: number;
  errors?: Array<{ name: string; error: string }>;
  importedIds: string[];
  message: string;
}

/**
 * Preview import parameters
 */
export interface PreviewImportParams {
  location: {
    lat: number;
    lon: number;
    name: string;
  };
  search_mode: 'text' | 'radius';
  radius_km?: number;
}

/**
 * Preview import response
 */
export interface PreviewImportResponse {
  hotels: HotelSearchResult[];
  existing_count: number;
  new_count: number;
  existing_venues: Record<string, ExistingVenueInfo>;
}

/**
 * Service for searching hotels via Google Places API
 */
export class GooglePlacesSearchService {
  /**
   * Search for hotels using text query
   * Supports pagination via nextPageToken
   * @param params - Search parameters
   * @returns Hotel search results with optional pagination token
   */
  async searchHotels(params: TextSearchParams): Promise<HotelSearchResponse> {
    const response = await apiFetch('/api/google-places/search-hotels', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query: params.query,
        location: params.location ? {
          lat: params.location.lat,
          lon: params.location.lon,
          radiusMeters: params.location.radiusMeters || 15000,
        } : undefined,
        pageSize: params.pageSize || 20,
        pageToken: params.pageToken,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || 'Failed to search hotels');
    }

    const data = await response.json();
    return data.data;
  }

  /**
   * Search for nearby hotels within a radius
   * Does NOT support pagination (max 20 results)
   * @param params - Nearby search parameters
   * @returns Hotel search results (no pagination)
   */
  async searchNearbyHotels(params: NearbySearchParams): Promise<HotelSearchResponse> {
    const response = await apiFetch('/api/google-places/search-nearby-hotels', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        location: {
          lat: params.location.lat,
          lon: params.location.lon,
          radiusMeters: params.location.radiusMeters || 15000,
        },
        maxResults: params.maxResults || 20,
        rankBy: params.rankBy || 'POPULARITY',
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || 'Failed to search nearby hotels');
    }

    const data = await response.json();
    return data.data;
  }

  /**
   * Get coordinates for a place_id
   * @param placeId - Google Places place_id
   * @returns Location coordinates and details
   */
  async geocodePlace(placeId: string): Promise<GeocodeResult> {
    const response = await apiFetch('/api/google-places/geocode-location', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ placeId }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || 'Failed to geocode location');
    }

    const data = await response.json();
    return data.data;
  }

  /**
   * Geocode a city by name using Google Places text search
   * Returns the best match with place_id and location data
   * @param city - City name
   * @param country - Country name
   * @returns City geocode result or null if not found
   */
  async geocodeCity(city: string, country: string): Promise<{
    place_id: string;
    city: string;
    country: string;
    formatted_address: string;
    location_json: any;
  } | null> {
    const response = await apiFetch('/api/google-places/geocode-city', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ city, country }),
    });

    if (!response.ok) {
      let errorMessage = 'Failed to geocode city';
      try {
        const errorData = await response.json();
        // Try to extract detailed error message from various possible structures
        if (errorData.error?.message) {
          errorMessage = errorData.error.message;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = typeof errorData.error === 'string' 
            ? errorData.error 
            : JSON.stringify(errorData.error);
        } else if (errorData.details) {
          errorMessage = typeof errorData.details === 'string'
            ? errorData.details
            : JSON.stringify(errorData.details);
        }
      } catch {
        // If JSON parsing fails, try to get status text
        errorMessage = `Failed to geocode city (HTTP ${response.status}: ${response.statusText || 'Unknown error'})`;
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data.data; // null if not found
  }

  /**
   * Build a search query for hotels near a location
   * @param locationName - Name of the city/area
   * @returns Formatted search query
   */
  buildHotelQuery(locationName?: string): string {
    if (locationName) {
      return `hotels near ${locationName}`;
    }
    return 'hotels';
  }

  /**
   * Check which Google Place IDs already exist in our database
   * Batch operation for efficiency - single query
   * @param placeIds - Array of Google Place IDs to check
   * @returns Map of placeId -> venue info for existing venues
   */
  async checkExistingVenues(placeIds: string[]): Promise<CheckExistingResponse> {
    if (placeIds.length === 0) {
      return { existing: {}, existingCount: 0 };
    }

    const response = await apiFetch('/api/google-places/check-existing', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ placeIds }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || 'Failed to check existing venues');
    }

    const data = await response.json();
    return data.data;
  }

  /**
   * Import hotels from Google Places into the venues database
   * @param hotels - Array of hotels to import
   * @returns Import result with counts
   */
  async importHotels(hotels: HotelImportData[]): Promise<ImportHotelsResponse> {
    if (hotels.length === 0) {
      return { imported: 0, skipped: 0, importedIds: [], message: 'No hotels to import' };
    }

    const response = await apiFetch('/api/google-places/import-hotels', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ hotels }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || 'Failed to import hotels');
    }

    const data = await response.json();
    return data.data;
  }

  /**
   * Preview import - searches Google Places and checks which hotels already exist
   * Used by Add Destination modal to show preview before actual import
   * @param params - Preview parameters including location and search mode
   * @returns Hotels found with existing/new counts
   */
  async previewImport(params: PreviewImportParams): Promise<PreviewImportResponse> {
    const response = await apiFetch('/api/google-places/preview-import', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        location: params.location,
        search_mode: params.search_mode,
        radius_km: params.radius_km,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || 'Failed to preview import');
    }

    const data = await response.json();
    return data.data;
  }
}

// Export singleton instance
export const googlePlacesSearchService = new GooglePlacesSearchService();

